/**
 * Fetch policy for the signal board's R7 graph (leg `gui-signal-board`). Framework-free so it is tested
 * with fake timers; the React hook is a thin wrapper.
 *
 *  - `refresh()` is called when the snapshot changed (the reducer already refetches R3 on `item_built`,
 *    `item_failed`, `level_done`, `level_check_settled`, `build_parked`, `build_terminal`, `plan_revised`,
 *    `task_transitioned`, so a changed snapshot IS one of those frames) and once on mount.
 *  - A slow poll (default 5 s) runs ONLY while `setActive(true)`: nothing is emitted when an item starts.
 *    It is a chained timeout, not an interval, armed after each request settles — so it can never
 *    overlap itself or pile up behind a slow GET.
 *  - At most ONE request in flight. Calls that land meanwhile collapse into a single follow-up request.
 *  - `since` is sent only when asked (`structural`: the plan gained a version) and only when a graph is
 *    held; R7 answers 404 `PLAN_VERSION_NOT_FOUND` for a `since` not below the served version, so that
 *    single case retries once without `since`.
 *  - A failed or 404 fetch never discards the last good graph, and a response older than the held graph
 *    (lower plan version) is ignored: the board keeps showing what it had.
 */
import type { TaskGraph } from "../protocol";
import { isLaneV2Error } from "./api";

export type GraphLoadStatus = "idle" | "loading" | "ok" | "missing" | "error";

export interface TaskGraphState {
  graph: TaskGraph | null;
  status: GraphLoadStatus;
  /** Node ids that appeared since the previous held version (highlight; empty when nothing was added). */
  addedIds: readonly string[];
}

export const INITIAL_GRAPH_STATE: TaskGraphState = { graph: null, status: "idle", addedIds: [] };

/** Poll period while a build is active. */
export const GRAPH_POLL_MS = 5000;

export interface TaskGraphControllerDeps {
  fetchGraph: (q: { since?: number }) => Promise<TaskGraph>;
  onState: (state: TaskGraphState) => void;
  pollMs?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export interface TaskGraphController {
  refresh(opts?: { structural?: boolean }): void;
  setActive(active: boolean): void;
  dispose(): void;
  state(): TaskGraphState;
}

/**
 * Fold a response into the held state. The body always carries every node and edge of the served
 * version, so "merging" the `since` diff means: take the newer body whole, never an older one, and use
 * the diff (plus an id comparison, for a response fetched without `since`) only to say what is new.
 */
export function mergeGraph(prev: TaskGraphState, next: TaskGraph): TaskGraphState {
  const held = prev.graph;
  if (held && next.version < held.version) return { ...prev, status: "ok" };
  let addedIds: readonly string[] = held && next.version === held.version ? prev.addedIds : [];
  if (held && next.version > held.version) {
    const had = new Set((held.nodes ?? []).map((n) => n.item_id));
    const added = new Set((next.nodes ?? []).map((n) => n.item_id).filter((id) => !had.has(id)));
    for (const n of next.since?.added_nodes ?? []) added.add(n.item_id);
    const removed = new Set(next.since?.removed_nodes ?? []);
    addedIds = [...added].filter((id) => !removed.has(id)).sort();
  }
  return { graph: next, status: "ok", addedIds };
}

export function createTaskGraphController(deps: TaskGraphControllerDeps): TaskGraphController {
  const pollMs = deps.pollMs ?? GRAPH_POLL_MS;
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  let current: TaskGraphState = INITIAL_GRAPH_STATE;
  let inFlight = false;
  let dirty = false;
  let dirtyStructural = false;
  let active = false;
  let disposed = false;
  let timer: unknown = null;

  const emit = (next: TaskGraphState) => {
    current = next;
    if (!disposed) deps.onState(next);
  };
  const disarm = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };
  const arm = () => {
    disarm();
    if (!active || disposed) return;
    timer = setTimer(() => {
      timer = null;
      refresh();
    }, pollMs);
  };

  async function run(structural: boolean): Promise<void> {
    inFlight = true;
    try {
      if (current.status === "idle") emit({ ...current, status: "loading" });
      const since = structural && current.graph ? current.graph.version : undefined;
      let body: TaskGraph;
      try {
        body = await deps.fetchGraph(since === undefined ? {} : { since });
      } catch (err) {
        if (since !== undefined && isLaneV2Error(err) && err.code === "PLAN_VERSION_NOT_FOUND") body = await deps.fetchGraph({});
        else throw err;
      }
      if (!disposed) emit(mergeGraph(current, body));
    } catch (err) {
      // A 404 means "no graph to show" (task or plan not there); anything else is an error. Either way the
      // last good graph stays on screen.
      const missing = isLaneV2Error(err) && err.status === 404;
      if (!missing) console.warn("[lane-v2] graph fetch failed", err);
      emit({ ...current, status: missing ? "missing" : "error" });
    } finally {
      inFlight = false;
    }
    if (disposed) return;
    if (dirty) {
      const again = dirtyStructural;
      dirty = false;
      dirtyStructural = false;
      void run(again);
      return;
    }
    arm();
  }

  function refresh(opts: { structural?: boolean } = {}): void {
    if (disposed) return;
    if (inFlight) {
      dirty = true;
      dirtyStructural = dirtyStructural || !!opts.structural;
      return;
    }
    disarm();
    void run(!!opts.structural);
  }

  return {
    refresh,
    setActive(next) {
      if (next === active) return;
      active = next;
      if (!active) disarm();
      else if (!inFlight && timer === null) arm();
    },
    dispose() {
      disposed = true;
      disarm();
    },
    state: () => current,
  };
}
